import type { CraCategory, CraRole, CraStep } from '../api/types'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * CRA register: the words the pages share — roles, categories, the art. 14
 * steps and their states, who says a vulnerability is exploited. See
 * cra_service on the server.
 */

export const RUOLI: Array<[CraRole, () => string, () => string]> = [
  ['manufacturer', () => t('Fabbricante'), () => t('Mette il prodotto sul mercato col proprio nome: segnala al CSIRT coordinatore e a ENISA (art. 14).')],
  ['steward', () => t('Steward open source'), () => t('Sostiene un software libero destinato ad attività commerciali: segnala nella misura in cui partecipa allo sviluppo (art. 24).')],
  ['importer', () => t('Importatore'), () => t('Mette sul mercato UE un prodotto di un fabbricante extra UE: informa il fabbricante e, se il rischio è significativo, le autorità (art. 19).')],
  ['distributor', () => t('Distributore'), () => t('Rende disponibile un prodotto altrui: informa il fabbricante e, se il rischio è significativo, le autorità (art. 20).')],
]

export const CATEGORIE: Array<[CraCategory, () => string]> = [
  ['default', () => t('Predefinita')],
  ['important_1', () => t('Importante, classe I (allegato III)')],
  ['important_2', () => t('Importante, classe II (allegato III)')],
  ['critical', () => t('Critica (allegato IV)')],
]

export function etichettaRuolo(r: string): string {
  return RUOLI.find(([k]) => k === r)?.[1]() ?? r
}

export function etichettaCategoria(c: string): string {
  return CATEGORIE.find(([k]) => k === c)?.[1]() ?? c
}

export function etichettaPasso(s: string, tipo: 'vulnerability' | 'incident' = 'vulnerability'): string {
  return ({
    early_warning: t('Preallerta (24 ore)'),
    notification: t('Notifica (72 ore)'),
    intermediate: t('Relazione intermedia (su richiesta del CSIRT)'),
    final_report: tipo === 'incident' ? t('Rapporto finale (un mese dalla notifica)') : t('Rapporto finale (14 giorni dalla misura correttiva)'),
    users_informed: t('Informazione agli utenti'),
    manufacturer_informed: t('Informazione al fabbricante'),
    authorities_informed: t('Informazione alle autorità di vigilanza del mercato'),
  } as Record<string, string>)[s] ?? s
}

/** What each step must contain (art. 14), as a reminder next to the form. */
export function contenutoPasso(s: string, tipo: 'vulnerability' | 'incident'): string {
  if (tipo === 'incident') {
    return ({
      early_warning: t('Se l’incidente è sospettato di essere causato da atti illeciti o malevoli e gli Stati membri in cui il prodotto è disponibile (art. 14(4)(a)).'),
      notification: t('Informazioni generali sulla natura dell’incidente, una prima valutazione, le misure correttive o di mitigazione prese e quelle che gli utenti possono prendere, la sensibilità delle informazioni (art. 14(4)(b)).'),
      final_report: t('Descrizione dettagliata dell’incidente con gravità e impatto, tipo di minaccia o causa probabile, misure di mitigazione applicate e in corso (art. 14(4)(c)).'),
    } as Record<string, string>)[s] ?? ''
  }
  return ({
    early_warning: t('Gli Stati membri in cui il prodotto è stato reso disponibile, se noti (art. 14(2)(a)).'),
    notification: t('Informazioni generali sul prodotto, la natura dello sfruttamento e della vulnerabilità, le misure correttive o di mitigazione prese e quelle che gli utenti possono prendere, la sensibilità delle informazioni (art. 14(2)(b)).'),
    final_report: t('Descrizione della vulnerabilità con gravità e impatto, informazioni su chi la sfrutta se disponibili, dettagli dell’aggiornamento di sicurezza o delle altre misure correttive (art. 14(2)(c)).'),
    users_informed: t('Gli utenti interessati, e se serve tutti, sulla vulnerabilità e sulle misure per mitigarla, in formato strutturato e leggibile da macchina (art. 14(8)): l’avviso CSAF si scarica qui.'),
    manufacturer_informed: t('Al fabbricante, senza indebito ritardo: la vulnerabilità, il prodotto e le versioni interessate.'),
    authorities_informed: t('Solo se il rischio di cibersicurezza è significativo: le autorità di vigilanza del mercato degli Stati membri in cui il prodotto è disponibile.'),
  } as Record<string, string>)[s] ?? ''
}

const STATI_PASSO: Record<CraStep['state'], [string, () => string]> = {
  done: ['badge--ok', () => t('inviata')],
  done_late: ['badge--warn', () => t('inviata in ritardo')],
  due: ['badge--info', () => t('da inviare')],
  overdue: ['badge--error', () => t('scaduta')],
  waiting_awareness: ['badge--muted', () => t('in attesa della presa di conoscenza')],
  on_request: ['badge--muted', () => t('solo su richiesta')],
  after_mitigation: ['badge--muted', () => t('dopo la misura correttiva')],
  no_deadline: ['badge--muted', () => t('senza termine fisso')],
}

/** The state of a step, with the time left when it is running. */
export function BadgePasso({ p }: { p: Pick<CraStep, 'state' | 'hours_left'> }) {
  const [tono, etichetta] = STATI_PASSO[p.state] ?? ['badge--muted', () => p.state]
  const resto = p.state === 'due' && p.hours_left != null
    ? ` · ${p.hours_left >= 48 ? t('{n} giorni', { n: Math.floor(p.hours_left / 24) }) : t('{n} ore', { n: Math.max(0, Math.round(p.hours_left)) })}`
    : ''
  return <span className={`badge ${tono}`}>{etichetta()}{resto}</span>
}

export function FontiSfruttata({ fonti, euvd }: { fonti: string[]; euvd?: string }) {
  return (
    <>
      {fonti.includes('kev') && <span className="badge badge--error" title={t('Catalogo CISA delle vulnerabilità sfruttate')}>CISA KEV</span>}
      {fonti.includes('kev') && fonti.includes('euvd') && ' '}
      {fonti.includes('euvd') && <span className="badge badge--error" title={euvd || t('Elenco ENISA delle vulnerabilità sfruttate')}>ENISA EUVD</span>}
    </>
  )
}

export const quandoCra = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString(localeCorrente(), { dateStyle: 'short', timeStyle: 'short' }) : '—'

export const giornoCra = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString(localeCorrente()) : '—'

/** A datetime-local value (local time) for an ISO instant, and back. */
export function locale(iso: string | null | undefined): string {
  const d = iso ? new Date(iso) : new Date()
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
}

export function daLocale(v: string): string {
  return new Date(v).toISOString()
}
